import React, { FC } from 'react';
import styled from 'styled-components';
import Icon from '~ComponentsImproved/Icon';
import Trophy from '~Assets/icons/trophy.svg';
import DollarCircle from '~Assets/icons/dollar-circle.svg';
import { Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import useModal from '~ComponentsImproved/Modal/useModal';
import useI18n from '~Translations';
import { useCompetition } from '~Providers/CompetitionsProvider';
import { handyBreakpoint } from '~Styles/breakpoints';
import { sortParticipantsBasedOnCompType } from '~Providers/CompetitionsProvider/utils';
import { useSelector } from '~Hooks';
import { useCurrency } from '~Hooks';
import { GetCompetitions_Venue_competitions_score } from '~Providers/CompetitionsProvider/__queries__';
import {
  formatPosition,
  getCorrectValueBasedOnCompType,
} from '~Providers/CompetitionsProvider/utils';
import { GetStaff_user_company_staffs } from '../__queries__';

const GridContainer = styled(Box)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: 1fr; // Ensure all rows have the same height

  ${handyBreakpoint} {
    grid-template-columns: 1fr;
  }
`;

const PlacementWrapper = styled(Box)<{ borderColor?: string }>`
  background-color: var(--colorBlueGray1000);
  border-radius: 8px;
  border: 1px solid ${({ borderColor }) => borderColor || 'var(--colorBlueGray1000)'};
  box-sizing: border-box;
`;

// Function to get the color based on the staffId
const getColor = (staffId: string, sortedScores: GetCompetitions_Venue_competitions_score[]) => {
  if (sortedScores[0]?.staffId === staffId) return '#FFD700'; // Gold
  if (sortedScores[1]?.staffId === staffId) return '#C0C0C0'; // Silver
  if (sortedScores[2]?.staffId === staffId) return '#CD7F32'; // Bronze
  return undefined; // Default color if not in top three
};

interface Props {
  staff: GetStaff_user_company_staffs[];
}

const MultipleCompetitions: FC<Props> = ({ staff }) => {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const staffId = useSelector((state) => state.staff?.id);
  const competitions = useCompetition();

  const isHandyBreakpoint = window.innerWidth <= 600;

  return (
    <GridContainer gap={isHandyBreakpoint ? 'medium' : 'large'}>
      {competitions?.onGoing.map((competition) => {
        const hasOnlySpecificSales =
          competition.onlySpecificSales.items.length > 0 ||
          competition.onlySpecificSales.categories.length > 0;

        const sortedScores = sortParticipantsBasedOnCompType(
          competition.score ? [...competition.score] : [],
          competition.type,
          hasOnlySpecificSales,
        );

        const thisParticipant = sortedScores.find((score) => score.staffId === staffId);
        const thisParticpantPlacement =
          sortedScores.findIndex((score) => score.staffId === staffId) + 1; // Adjust index to position
        if (!thisParticipant) return null;

        const thisParticipantScore = getCorrectValueBasedOnCompType(
          thisParticipant,
          competition.type,
          hasOnlySpecificSales,
          currency.format,
          i18n,
        );

        const participantAbove =
          thisParticpantPlacement !== 1 &&
          staff?.find((staff) => staff.id === sortedScores[thisParticpantPlacement - 2].staffId);
        const participantAboveScore =
          participantAbove &&
          getCorrectValueBasedOnCompType(
            sortedScores[thisParticpantPlacement - 2], // Minus two to adjust back to index - 1
            competition.type,
            hasOnlySpecificSales,
            currency.format,
            i18n,
          );

        if (!thisParticipant) return null;

        return (
          <PlacementWrapper
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            borderColor={getColor(staffId ?? '', sortedScores)}
            padding="medium"
            key={competition.id}>
            <Heading level={3} color="#fff" bottom={8}>
              {competition.name}
            </Heading>
            <Box display="flex" alignItems="center" gap="xs" marginBottom="xs">
              <Icon src={Trophy} color="var(--colorBlueGray200)" />
              <Paragraph level={4} color="var(--colorBlueGray200)">
                {formatPosition(thisParticpantPlacement, i18n)}
              </Paragraph>
            </Box>
            <Box display="flex" alignItems="center" gap="xs" marginBottom="xs">
              <Icon src={DollarCircle} color="var(--colorBlueGray200)" />
              <Paragraph level={4} color="var(--colorBlueGray200)">
                {thisParticipantScore}
              </Paragraph>
            </Box>

            {participantAbove && (
              <Paragraph level={5} color="var(--colorBlueGray200)">
                <b>{participantAbove.name}</b> {i18n('Competitions.Status.IsAboveYou')}{' '}
                <b>{participantAboveScore}</b>
              </Paragraph>
            )}
          </PlacementWrapper>
        );
      })}
    </GridContainer>
  );
};

export default MultipleCompetitions;
